import { Injectable, inject } from '@angular/core';
import { CommonIP } from '@model/ftf-api.model';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { DialogService } from '@service/dialog.service';
import { SettingsService } from '@service/settings.service';
import { of } from 'rxjs';
import { catchError, filter, map, mergeMap, take, withLatestFrom } from 'rxjs/operators';

import * as FavoritesActions from '../actions/favorites.actions';
import { selectAccountDetails, selectIsAccountDetailsLoaded } from '../selectors/account.selectors';
import { selectFavoriteItems } from '../selectors/favorites.selectors';

@Injectable()
export class FavoritesEffects {
  private actions$ = inject(Actions);
  private store = inject(Store);
  private settingsService = inject(SettingsService);
  private dialogService = inject(DialogService);

  loadFavorites$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FavoritesActions.loadFavorites),
      mergeMap(() =>
        this.store.select(selectIsAccountDetailsLoaded).pipe(
          filter((isLoaded) => isLoaded),
          take(1),
          withLatestFrom(this.store.select(selectAccountDetails)),
          map(([, accountDetails]) => {
            try {
              const userName = accountDetails?.userName ?? 'default';
              const clientName = accountDetails?.clientName ?? 'default';
              const key = this.settingsService.generateHexKey(userName, clientName, 'favorite');

              const favoriteData = localStorage.getItem(key);

              const favorites: CommonIP[] = favoriteData ? JSON.parse(favoriteData) : [];

              return FavoritesActions.loadFavoritesSuccess({ favorites });
            } catch (error) {
              return FavoritesActions.loadFavoritesFailure({ error });
            }
          }),
          catchError((error) => {
            return of(FavoritesActions.loadFavoritesFailure({ error }));
          })
        )
      )
    )
  );

  addFavoriteItem$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FavoritesActions.addFavoriteItem),
      withLatestFrom(this.store.select(selectFavoriteItems), this.store.select(selectAccountDetails)),
      mergeMap(([action, currentFavorites, accountDetails]) => {
        try {
          const isDuplicate = currentFavorites.some(
            (fav) => fav.identifier === action.favorite.identifier && fav.ipType === action.favorite.ipType
          );

          if (isDuplicate) {
            return of(FavoritesActions.addFavoriteItemSuccess({ favorites: currentFavorites }));
          }

          if (currentFavorites.length >= 10) {
            this.dialogService.openDialog({
              width: '250px',
              title: 'Favorites Help',
              message: 'Max number of 10 cases has been starred. To add more cases to favorites, please unstar a case.',
              button1: {
                text: 'OK',
                style: { color: 'primary' },
              },
            });

            return of(FavoritesActions.addFavoriteItemFailure({ error: 'Maximum favorites limit reached' }));
          }

          const newFavorite: CommonIP = {
            identifier: action.favorite.identifier,
            ipType: action.favorite.ipType,
            family: action.favorite.family,
            title: action.favorite.title ?? '',
            serial: action.favorite.serial ?? '',
          };

          const updatedFavorites = [newFavorite, ...currentFavorites];

          const userName = accountDetails?.userName ?? 'default';
          const clientName = accountDetails?.clientName ?? 'default';
          const key = this.settingsService.generateHexKey(userName, clientName, 'favorite');
          localStorage.setItem(key, JSON.stringify(updatedFavorites));

          return of(FavoritesActions.addFavoriteItemSuccess({ favorites: updatedFavorites }));
        } catch (error) {
          return of(FavoritesActions.addFavoriteItemFailure({ error }));
        }
      }),
      catchError((error) => of(FavoritesActions.addFavoriteItemFailure({ error })))
    )
  );

  removeFavoriteItem$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FavoritesActions.removeFavoriteItem),
      withLatestFrom(this.store.select(selectFavoriteItems), this.store.select(selectAccountDetails)),
      mergeMap(([action, currentFavorites, accountDetails]) => {
        try {
          const index = currentFavorites.findIndex(
            (fav) => fav.identifier === action.favorite.identifier && fav.ipType === action.favorite.ipType
          );

          if (index === -1) {
            return of(FavoritesActions.removeFavoriteItemSuccess({ favorites: currentFavorites }));
          }

          const updatedFavorites = [...currentFavorites];
          updatedFavorites.splice(index, 1);

          const userName = accountDetails?.userName ?? 'default';
          const clientName = accountDetails?.clientName ?? 'default';
          const key = this.settingsService.generateHexKey(userName, clientName, 'favorite');
          localStorage.setItem(key, JSON.stringify(updatedFavorites));

          return of(FavoritesActions.removeFavoriteItemSuccess({ favorites: updatedFavorites }));
        } catch (error) {
          return of(FavoritesActions.removeFavoriteItemFailure({ error }));
        }
      }),
      catchError((error) => of(FavoritesActions.removeFavoriteItemFailure({ error })))
    )
  );
}
